import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface AvatarProps {
  name?: string | null;
  email?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  src?: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
}

// Cores predefinidas para os avatares
const avatarColors = [
  '#FF6B6B', // Vermelho
  '#4ECDC4', // Turquesa
  '#45B7D1', // Azul
  '#96CEB4', // Verde
  '#FFEAA7', // Amarelo
  '#DDA0DD', // Ameixa
  '#98D8C8', // Verde água
  '#F7DC6F', // Dourado
  '#BB8FCE', // Roxo claro
  '#85C1E9', // Azul claro
  '#F8C471', // Laranja
  '#82E0AA', // Verde claro
  '#F1948A', // Rosa
  '#85C1E9', // Azul céu
  '#D7BDE2', // Lavanda
];

// Função para gerar cor baseada no nome/email
const getAvatarColor = (text: string): string => {
  if (!text) return avatarColors[0];
  
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % avatarColors.length;
  return avatarColors[index];
};

// Função para extrair iniciais
const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return words[0].substring(0, 2).toUpperCase();
  }
  
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  
  return 'U';
};

// Tamanhos do avatar
const sizeMap = {
  xs: '24px',
  sm: '32px',
  md: '48px',
  lg: '64px',
  xl: '96px',
  '2xl': '128px'
};

const fontSizeMap = {
  xs: '10px',
  sm: '12px',
  md: '16px',
  lg: '20px',
  xl: '28px',
  '2xl': '36px'
};

const AvatarContainer = styled.div<{ 
  $size: string; 
  $backgroundColor: string; 
  $clickable: boolean;
}>`
  width: ${props => props.$size};
  height: ${props => props.$size};
  border-radius: 50%;
  background: ${props => props.$backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${props => {
    const sizeKey = Object.keys(sizeMap).find(key => sizeMap[key as keyof typeof sizeMap] === props.$size) as keyof typeof fontSizeMap;
    return fontSizeMap[sizeKey] || '16px';
  }};
  overflow: hidden;
  position: relative;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  // Contorno profissional estilo Instagram
  box-shadow: 
    0 0 0 1px rgba(255, 255, 255, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.1);
  border: none !important;
  outline: none !important;

  &:hover {
    transform: ${props => props.$clickable ? 'scale(1.05)' : 'none'};
    box-shadow: 
      0 0 0 1px rgba(255, 255, 255, 0.4),
      0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: ${props => props.$clickable ? 'scale(0.98)' : 'none'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }


`;

// Componente para o logo do Loopra com cor personalizada
const LoopraLogo = styled.div<{
  $size: string;
  $backgroundColor: string;
}>`
  width: 100%;
  height: 100%;
  background-image: url('/icons/loopra_icon_profile.webp');
  background-size: 80%;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0) saturate(100%) invert(1);
  position: relative;
  
  // Overlay com a cor personalizada
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$backgroundColor};
    border-radius: 50%;
    z-index: -1;
    opacity: 0.98;
  }
  
  // Efeito de brilho sutil
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(0, 0, 0, 0.05) 100%);
    border-radius: 50%;
    z-index: 1;
  }
`;

const Avatar: React.FC<AvatarProps> = ({
  name,
  email,
  size = 'md',
  src,
  alt,
  className,
  onClick
}) => {
  const displayName = name || email || 'Usuário';
  const backgroundColor = getAvatarColor(displayName);
  const avatarSize = sizeMap[size];

  return (
    <AvatarContainer
      $size={avatarSize}
      $backgroundColor={backgroundColor}
      $clickable={!!onClick}
      className={className}
      onClick={onClick}
      title={displayName}
    >
      {src ? (
        <img src={src} alt={alt || displayName} />
      ) : (
        <LoopraLogo 
          $size={avatarSize} 
          $backgroundColor={backgroundColor} 
        />
      )}
    </AvatarContainer>
  );
};

export default Avatar;

